'use client';

import { useCallback, useState } from 'react';
import { useQueryClient, type QueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';
import { assignGtmSignal, assignSignalAccount, type WorkspaceUser } from '@/services/gtmSignals';
import { getUserDisplayName } from '@/lib/formatters/people';
import { getApiErrorMessage } from '@/lib/api/getApiErrorMessage';
import { assignProspectOwner } from '@/services/prospects';
import type { ProspectOwnership } from '@/types/prospects';

/**
 * What an owner change moves.
 *
 * - `account` — every signal on the domain (`assignSignalAccount`), narrowed to
 *   one person when `personKey` is set. The unit of a Signals hub row, which is
 *   every signal on the account: a per-signal call there would move some of
 *   them, report success, and leave the row showing its old owner.
 * - `signal` — one signal (`assignGtmSignal`). The unit of a surface that shows
 *   one person's request; the account path would hand a colleague's unrelated
 *   work on the same domain over too.
 * - `prospect` — only the person's Prospects owner, independent of signals,
 *   outreach senders, and connected CRM ownership.
 */
export type AssignOwnerTarget =
  | { scope: 'account'; companyDomain: string; personKey?: string | null }
  | { scope: 'signal'; signalId: string }
  | { scope: 'prospect'; prospectId: string };

export interface AssignOwnerResult {
  ok: boolean;
  /** The picked user, so the caller can show the new owner before any refetch lands. */
  owner: WorkspaceUser | null;
  ownership?: ProspectOwnership;
}

/**
 * Everything that reads assignment, invalidated after every owner change from
 * any surface: the hub's company and people rows, the Tasks lanes and counts,
 * an open detail panel (a surface that does not unmount on reassign keeps
 * rendering the stale assignee otherwise) and the SDR dashboard.
 *
 * One list rather than one per caller: each caller used to refresh only its own
 * view, so a reassign from Tasks left the hub row showing the old owner.
 */
export const ASSIGNMENT_QUERY_KEYS: readonly QueryKey[] = [
  ['unified-signals'],
  ['unified-signals-people'],
  ['gtm-signal-personas'],
  ['gtm-signal-persona-counts'],
  ['gtm-signal-detail'],
  ['sdr-dashboard'],
  ['prospects'],
  ['prospect-detail'],
];

interface AssignOwnerInput {
  target: AssignOwnerTarget;
  /** The picked user id, or null for Unassigned. */
  ownerId: string | null;
  /** The roster the picker offered, used to name the new owner. */
  roster: readonly WorkspaceUser[];
  queryClient: Pick<QueryClient, 'invalidateQueries'>;
  /**
   * The caller's own view, when it is not one of the shared keys above.
   * Awaited, unlike the shared keys: the caller's surface is the one the rep is
   * looking at, and clearing its pending state before that refetch lands shows
   * the previous owner for a beat.
   */
  extraQueryKeys?: readonly QueryKey[];
}

/**
 * Change who owns a signal or an account, tell the rep, and refresh every view
 * that reads ownership.
 *
 * The non-hook core, for callers that already hold a query client
 * (`reassignSignalOwner`); components use {@link useAssignOwner}.
 *
 * Drafts are not rewritten from here. Generated copy is sender-agnostic and a
 * rep-to-rep move fires the gtm_signals owner-change trigger, whose outbox the
 * content worker drains to regenerate the outreach drafts (ap#3185).
 */
export async function assignOwner({
  target, ownerId, roster, queryClient, extraQueryKeys = [],
}: AssignOwnerInput): Promise<AssignOwnerResult> {
  const picked = ownerId ? roster.find((user) => user.user_id === ownerId) ?? null : null;
  const who = picked ? getUserDisplayName(picked) : null;

  let message: string;
  let ownership: ProspectOwnership | undefined;
  try {
    if (target.scope === 'prospect') {
      ownership = await assignProspectOwner(target.prospectId, ownerId);
      message = ownerId ? `Prospect assigned${who ? ` to ${who}` : ''}` : 'Prospect unassigned';
    } else if (target.scope === 'account') {
      const result = await assignSignalAccount({
        companyDomain: target.companyDomain,
        personKey: target.personKey ?? null,
        assignedTo: ownerId,
      });
      // The count is in the message because "assigned" with no number hides a
      // partial result, which is what this endpoint exists to avoid.
      const n = `${result.signalsUpdated} signal${result.signalsUpdated === 1 ? '' : 's'}`;
      message = ownerId ? `${n} assigned${who ? ` to ${who}` : ''}` : `${n} unassigned`;
    } else {
      await assignGtmSignal(target.signalId, ownerId);
      message = ownerId ? (who ? `Assigned to ${who}` : 'Assigned') : 'Unassigned';
    }
  } catch (error) {
    toast.error(getApiErrorMessage(error, target.scope === 'prospect'
      ? "Couldn't assign this prospect. Please try again."
      : target.scope === 'account'
      ? "Couldn't reassign this account. Nothing was changed."
      : "Couldn't reassign this request. Nothing was changed.", ['response.error']));
    return { ok: false, owner: null };
  }

  toast.success(message);
  for (const queryKey of ASSIGNMENT_QUERY_KEYS) {
    void queryClient.invalidateQueries({ queryKey: [...queryKey] });
  }
  await Promise.all(extraQueryKeys.map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })));
  return { ok: true, owner: picked, ...(ownership ? { ownership } : {}) };
}

interface UseAssignOwnerOptions {
  roster: readonly WorkspaceUser[];
  extraQueryKeys?: readonly QueryKey[];
}

/**
 * {@link assignOwner} with the app's query client and a pending flag for the
 * picker. Every owner change in the app goes through this, so the toast, the
 * error copy and the refresh are the same wherever a rep reassigns from.
 */
export function useAssignOwner({ roster, extraQueryKeys }: UseAssignOwnerOptions) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState(false);

  const assign = useCallback(async (target: AssignOwnerTarget, ownerId: string | null) => {
    setPending(true);
    try {
      return await assignOwner({ target, ownerId, roster, queryClient, extraQueryKeys });
    } finally {
      setPending(false);
    }
  }, [extraQueryKeys, queryClient, roster]);

  return { assign, pending };
}
